<template>
  <div class="etq-inspector">
    <div v-if="!element" class="inspector-empty">
      <div class="inspector-empty-icon">
        <i class="fas fa-mouse-pointer"></i>
      </div>
      <h6>Selecione um elemento</h6>
      <p>
        Clique em algo na etiqueta para ajustar conteúdo, tamanho e aparência.
      </p>
    </div>

    <template v-else>
      <div class="inspector-header">
        <div class="min-w-0">
          <span class="etq-panel-kicker">Elemento selecionado</span>
          <input
            :value="element.nome"
            class="element-name"
            maxlength="80"
            aria-label="Nome do elemento"
            @change="set('nome', $event.target.value)"
          />
        </div>
        <div class="d-flex flex-shrink-0">
          <button
            type="button"
            class="btn btn-xs btn-icon btn-light mr-1"
            :title="
              element.visivel === false
                ? 'Mostrar elemento'
                : 'Ocultar elemento'
            "
            @click="set('visivel', element.visivel === false)"
          >
            <i
              :class="
                element.visivel === false ? 'far fa-eye-slash' : 'far fa-eye'
              "
            ></i>
          </button>
          <button
            type="button"
            class="btn btn-xs btn-icon btn-light mr-1"
            title="Duplicar"
            @click="$emit('duplicate')"
          >
            <i class="far fa-copy"></i>
          </button>
          <button
            type="button"
            class="btn btn-xs btn-icon btn-light-danger"
            title="Remover"
            @click="$emit('delete')"
          >
            <i class="far fa-trash-alt"></i>
          </button>
        </div>
      </div>

      <b-tabs pills nav-class="inspector-tabs" content-class="pt-4" small fill>
        <b-tab title="Conteúdo" active>
          <div v-if="hasField" class="form-group">
            <label>Campo exibido</label>
            <select
              class="form-control form-control-sm"
              :value="element.campo"
              @change="setField($event.target.value)"
            >
              <optgroup
                v-for="group in fieldGroups"
                :key="group.name"
                :label="group.name"
              >
                <option
                  v-for="field in group.fields"
                  :key="field.key"
                  :value="field.key"
                >
                  {{ field.label }}
                </option>
              </optgroup>
            </select>
          </div>

          <div v-if="element.tipo === 'text'" class="form-group">
            <label>Texto</label>
            <textarea
              :value="element.texto"
              class="form-control form-control-sm"
              rows="3"
              maxlength="250"
              placeholder="Digite o conteúdo"
              @input="set('texto', $event.target.value)"
            ></textarea>
          </div>

          <div v-if="hasField || element.tipo === 'text'" class="form-group">
            <label>Mostrar este elemento</label>
            <select
              class="form-control form-control-sm"
              :value="element.exibir_quando"
              @change="set('exibir_quando', $event.target.value)"
            >
              <option
                v-for="rule in displayRules"
                :key="rule.value"
                :value="rule.value"
              >
                {{ rule.label }}
              </option>
            </select>
          </div>

          <template v-if="element.tipo === 'barcode'">
            <div class="form-group">
              <label>Tipo do código</label>
              <select
                class="form-control form-control-sm"
                :value="get('codigo_barras.tipo', 'auto')"
                @change="set('codigo_barras.tipo', $event.target.value)"
              >
                <option
                  v-for="type in barcodeTypes"
                  :key="type.value"
                  :value="type.value"
                >
                  {{ type.label }}
                </option>
              </select>
            </div>
            <label class="switch-row">
              <span>
                <strong>Exibir número</strong>
                <small>Mostra o número abaixo das barras</small>
              </span>
              <b-form-checkbox
                switch
                :checked="get('codigo_barras.exibir_texto', true)"
                @change="set('codigo_barras.exibir_texto', $event)"
              ></b-form-checkbox>
            </label>
          </template>

          <template v-if="isSalePrice">
            <div class="form-group">
              <label>Modo de exibição</label>
              <select
                class="form-control form-control-sm"
                :value="get('priceMode', 'cash')"
                @change="setPriceMode($event.target.value)"
              >
                <option value="cash">Somente à vista</option>
                <option value="installment">Somente parcelado</option>
                <option value="cash_installment">À vista + parcelado</option>
              </select>
            </div>
            <template v-if="priceModeHasInstallments">
              <div class="form-group">
                <label>Quantidade de parcelas</label>
                <input
                  type="number"
                  min="1"
                  step="1"
                  class="form-control form-control-sm"
                  :value="get('installments', 3)"
                  @change="setNumber('installments', $event.target.value)"
                />
              </div>
              <div class="form-group">
                <label>Acréscimo no preço</label>
                <div class="input-group input-group-sm">
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    class="form-control"
                    :value="get('installmentIncrease', 0)"
                    @change="
                      setNumber('installmentIncrease', $event.target.value)
                    "
                  />
                  <div class="input-group-append">
                    <span class="input-group-text">%</span>
                  </div>
                </div>
              </div>
              <div class="form-group">
                <label>Finalização do preço</label>
                <select
                  class="form-control form-control-sm"
                  :value="get('commercialEnding', 'none')"
                  @change="set('commercialEnding', $event.target.value)"
                >
                  <option value="none">Sem ajuste</option>
                  <option value="0">Terminar em 0</option>
                  <option value="9">Terminar em 9</option>
                </select>
              </div>
            </template>
          </template>

          <div v-if="hasTextFormat" class="format-box">
            <strong>Formatação do valor</strong>
            <div class="row mt-3">
              <div class="col-6 form-group mb-3">
                <label>Antes</label>
                <input
                  :value="get('formato.prefixo', '')"
                  class="form-control form-control-sm"
                  placeholder="Ex.: R$"
                  @input="set('formato.prefixo', $event.target.value)"
                />
              </div>
              <div class="col-6 form-group mb-3">
                <label>Depois</label>
                <input
                  :value="get('formato.sufixo', '')"
                  class="form-control form-control-sm"
                  placeholder="Ex.: /un"
                  @input="set('formato.sufixo', $event.target.value)"
                />
              </div>
            </div>
            <div class="form-group mb-3">
              <label>Maiúsculas e minúsculas</label>
              <select
                class="form-control form-control-sm"
                :value="get('formato.transformacao', 'nenhuma')"
                @change="set('formato.transformacao', $event.target.value)"
              >
                <option value="nenhuma">Manter como está</option>
                <option value="maiusculas">TUDO MAIÚSCULO</option>
                <option value="minusculas">tudo minúsculo</option>
                <option value="capitalizar">Iniciais Maiúsculas</option>
              </select>
            </div>
            <div class="row">
              <div class="col-6 form-group mb-0">
                <label>Limite de caracteres</label>
                <input
                  type="number"
                  min="0"
                  max="250"
                  class="form-control form-control-sm"
                  :value="get('formato.limite_caracteres', 0)"
                  @input="
                    setNumber('formato.limite_caracteres', $event.target.value)
                  "
                />
              </div>
              <div class="col-6 form-group mb-0">
                <label>Se estiver vazio</label>
                <input
                  class="form-control form-control-sm"
                  :value="get('formato.texto_vazio', '')"
                  placeholder="Não mostrar"
                  @input="set('formato.texto_vazio', $event.target.value)"
                />
              </div>
            </div>
          </div>
        </b-tab>

        <b-tab title="Aparência">
          <template v-if="hasAppearance">
            <div class="row">
              <div class="col-7 form-group">
                <label>Fonte</label>
                <select
                  class="form-control form-control-sm"
                  :value="get('estilo.fonte', 'dejavu_sans')"
                  @change="set('estilo.fonte', $event.target.value)"
                >
                  <option
                    v-for="font in fonts"
                    :key="font.value"
                    :value="font.value"
                  >
                    {{ font.label }}
                  </option>
                </select>
              </div>
              <div class="col-5 form-group">
                <label>Tamanho</label>
                <div class="input-group input-group-sm">
                  <input
                    type="number"
                    min="4"
                    max="72"
                    step="0.5"
                    class="form-control"
                    :value="get('estilo.tamanho_pt', 8)"
                    @input="setNumber('estilo.tamanho_pt', $event.target.value)"
                  />
                  <div class="input-group-append">
                    <span class="input-group-text">pt</span>
                  </div>
                </div>
              </div>
            </div>

            <label>Estilo do texto</label>
            <div class="style-toolbar mb-4">
              <button
                type="button"
                :class="{ active: get('estilo.negrito', false) }"
                title="Negrito"
                @click="toggle('estilo.negrito')"
              >
                <strong>B</strong>
              </button>
              <button
                type="button"
                :class="{ active: get('estilo.italico', false) }"
                title="Itálico"
                @click="toggle('estilo.italico')"
              >
                <em>I</em>
              </button>
              <button
                type="button"
                :class="{ active: get('estilo.sublinhado', false) }"
                title="Sublinhado"
                @click="toggle('estilo.sublinhado')"
              >
                <u>U</u>
              </button>
            </div>

            <div v-if="hasContentAlignment" class="content-alignment-box mb-4">
              <div class="content-alignment-heading">
                <strong>Alinhamento dentro da caixa</strong>
                <small>
                  Ajusta o conteúdo sem mover a caixa na etiqueta.
                </small>
              </div>

              <label>Horizontal</label>
              <div class="alignment-toolbar mb-3">
                <button
                  type="button"
                  class="content-align-left"
                  :class="{
                    active: get('estilo.alinhamento', 'left') === 'left',
                  }"
                  title="Conteúdo à esquerda"
                  @click="set('estilo.alinhamento', 'left')"
                >
                  <i class="fas fa-align-left"></i>
                  <span>Esquerda</span>
                </button>
                <button
                  type="button"
                  class="content-align-center"
                  :class="{
                    active: get('estilo.alinhamento', 'left') === 'center',
                  }"
                  title="Conteúdo centralizado horizontalmente"
                  @click="set('estilo.alinhamento', 'center')"
                >
                  <i class="fas fa-align-center"></i>
                  <span>Centro</span>
                </button>
                <button
                  type="button"
                  class="content-align-right"
                  :class="{
                    active: get('estilo.alinhamento', 'left') === 'right',
                  }"
                  title="Conteúdo à direita"
                  @click="set('estilo.alinhamento', 'right')"
                >
                  <i class="fas fa-align-right"></i>
                  <span>Direita</span>
                </button>
              </div>

              <label>Vertical</label>
              <div class="alignment-toolbar">
                <button
                  type="button"
                  class="content-align-top"
                  :class="{
                    active:
                      get('estilo.alinhamento_vertical', 'middle') === 'top',
                  }"
                  title="Conteúdo no topo"
                  @click="set('estilo.alinhamento_vertical', 'top')"
                >
                  <i class="fas fa-arrow-up"></i>
                  <span>Topo</span>
                </button>
                <button
                  type="button"
                  class="content-align-middle"
                  :class="{
                    active:
                      get('estilo.alinhamento_vertical', 'middle') === 'middle',
                  }"
                  title="Conteúdo centralizado verticalmente"
                  @click="set('estilo.alinhamento_vertical', 'middle')"
                >
                  <i class="fas fa-arrows-alt-v"></i>
                  <span>Centro</span>
                </button>
                <button
                  type="button"
                  class="content-align-bottom"
                  :class="{
                    active:
                      get('estilo.alinhamento_vertical', 'middle') === 'bottom',
                  }"
                  title="Conteúdo na base"
                  @click="set('estilo.alinhamento_vertical', 'bottom')"
                >
                  <i class="fas fa-arrow-down"></i>
                  <span>Base</span>
                </button>
              </div>

              <small class="content-alignment-tip">
                Para mover ou centralizar a caixa inteira, use a aba Posição.
              </small>
            </div>

            <div class="row">
              <div class="col-6 form-group">
                <label>Cor do texto</label>
                <div class="color-field">
                  <input
                    type="color"
                    :value="colorValue(get('estilo.cor', '#000000'))"
                    @input="
                      set('estilo.cor', $event.target.value.toUpperCase())
                    "
                  />
                  <span>{{ colorValue(get("estilo.cor", "#000000")) }}</span>
                </div>
              </div>
              <div class="col-6 form-group">
                <label>Fundo</label>
                <div class="color-field">
                  <input
                    type="color"
                    :disabled="
                      get('estilo.fundo', 'transparent') === 'transparent'
                    "
                    :value="colorValue(get('estilo.fundo', '#FFFFFF'))"
                    @input="
                      set('estilo.fundo', $event.target.value.toUpperCase())
                    "
                  />
                  <button
                    type="button"
                    class="transparent-toggle"
                    :class="{
                      active:
                        get('estilo.fundo', 'transparent') === 'transparent',
                    }"
                    @click="toggleTransparent"
                  >
                    Sem fundo
                  </button>
                </div>
              </div>
            </div>

            <div class="row">
              <div class="col-6 form-group mb-0">
                <label>Borda</label>
                <div class="input-group input-group-sm">
                  <input
                    type="number"
                    min="0"
                    max="2"
                    step="0.1"
                    class="form-control"
                    :value="get('estilo.borda_mm', 0)"
                    @input="setNumber('estilo.borda_mm', $event.target.value)"
                  />
                  <div class="input-group-append">
                    <span class="input-group-text">mm</span>
                  </div>
                </div>
              </div>
              <div class="col-6 form-group mb-0">
                <label>Espaço interno</label>
                <div class="input-group input-group-sm">
                  <input
                    type="number"
                    min="0"
                    max="10"
                    step="0.1"
                    class="form-control"
                    :value="get('estilo.padding_mm', 0)"
                    @input="setNumber('estilo.padding_mm', $event.target.value)"
                  />
                  <div class="input-group-append">
                    <span class="input-group-text">mm</span>
                  </div>
                </div>
              </div>
            </div>
          </template>
          <div v-else class="inspector-note">
            Este tipo de elemento usa somente posição, tamanho e cor de borda.
          </div>
        </b-tab>

        <b-tab title="Posição">
          <div class="position-grid">
            <div class="form-group">
              <label>Esquerda (X)</label>
              <div class="input-group input-group-sm">
                <input
                  type="number"
                  step="0.1"
                  class="form-control"
                  :value="element.x_mm"
                  @input="setNumber('x_mm', $event.target.value)"
                />
                <div class="input-group-append">
                  <span class="input-group-text">mm</span>
                </div>
              </div>
            </div>
            <div class="form-group">
              <label>Topo (Y)</label>
              <div class="input-group input-group-sm">
                <input
                  type="number"
                  step="0.1"
                  class="form-control"
                  :value="element.y_mm"
                  @input="setNumber('y_mm', $event.target.value)"
                />
                <div class="input-group-append">
                  <span class="input-group-text">mm</span>
                </div>
              </div>
            </div>
            <div class="form-group">
              <label>Largura</label>
              <div class="input-group input-group-sm">
                <input
                  type="number"
                  min="0.2"
                  step="0.1"
                  class="form-control"
                  :value="element.largura_mm"
                  @input="setNumber('largura_mm', $event.target.value)"
                />
                <div class="input-group-append">
                  <span class="input-group-text">mm</span>
                </div>
              </div>
            </div>
            <div class="form-group">
              <label>Altura</label>
              <div class="input-group input-group-sm">
                <input
                  type="number"
                  min="0.2"
                  step="0.1"
                  class="form-control"
                  :value="element.altura_mm"
                  @input="setNumber('altura_mm', $event.target.value)"
                />
                <div class="input-group-append">
                  <span class="input-group-text">mm</span>
                </div>
              </div>
            </div>
          </div>

          <div class="form-group">
            <label>Rotação</label>
            <div class="rotation-buttons">
              <button
                v-for="rotation in [0, 90, 180, 270]"
                :key="rotation"
                type="button"
                :class="{ active: Number(element.rotacao) === rotation }"
                @click="set('rotacao', rotation)"
              >
                {{ rotation }}°
              </button>
            </div>
          </div>

          <div class="position-actions">
            <button
              type="button"
              class="center-horizontal-button btn btn-sm btn-light-primary font-weight-bold"
              @click="$emit('center-horizontal')"
            >
              <i class="fas fa-arrows-alt-h mr-2"></i>Centralizar
              horizontalmente
            </button>
            <button
              type="button"
              class="center-vertical-button btn btn-sm btn-light-primary font-weight-bold"
              @click="$emit('center-vertical')"
            >
              <i class="fas fa-arrows-alt-v mr-2"></i>Centralizar verticalmente
            </button>
            <button
              type="button"
              class="btn btn-sm btn-light-primary font-weight-bold"
              @click="$emit('front')"
            >
              <i class="fas fa-layer-group mr-2"></i>Trazer para frente
            </button>
          </div>
        </b-tab>
      </b-tabs>
    </template>
  </div>
</template>

<script>
import { clone } from "@/modules/Etiquetas/services/etiquetaDefinition";

export default {
  name: "EtiquetaElementInspector",
  props: {
    element: { type: Object, default: null },
    label: { type: Object, default: () => ({}) },
    fields: { type: Array, default: () => [] },
    fonts: { type: Array, default: () => [] },
    barcodeTypes: { type: Array, default: () => [] },
    displayRules: { type: Array, default: () => [] },
  },
  computed: {
    isSalePrice() {
      return (
        this.element?.tipo === "field" &&
        this.element?.campo === "produto.preco"
      );
    },
    priceModeHasInstallments() {
      return ["installment", "cash_installment"].includes(
        this.get("priceMode", "cash")
      );
    },
    hasField() {
      return (
        this.element &&
        ["field", "barcode", "qrcode"].includes(this.element.tipo)
      );
    },
    hasTextFormat() {
      return this.element && ["field", "text"].includes(this.element.tipo);
    },
    hasAppearance() {
      return (
        this.element &&
        !["qrcode", "logo", "line", "rectangle"].includes(this.element.tipo)
      );
    },
    hasContentAlignment() {
      return this.element && ["field", "text"].includes(this.element.tipo);
    },
    fieldGroups() {
      const groups = this.fields.reduce((result, field) => {
        const name = field.category || "Outros";
        if (!result[name]) result[name] = [];
        result[name].push(field);
        return result;
      }, {});
      return Object.keys(groups).map((name) => ({
        name,
        fields: groups[name],
      }));
    },
  },
  methods: {
    setField(field) {
      const next = clone(this.element);
      next.campo = field;
      if (
        next.tipo === "field" &&
        field === "produto.preco" &&
        !next.priceMode
      ) {
        next.priceMode = "cash";
        next.installments = 3;
        next.installmentIncrease = 0;
        next.commercialEnding = "none";
      }
      this.$emit("change-start");
      this.$emit("update", next);
    },
    setPriceMode(mode) {
      const next = clone(this.element);
      next.priceMode = mode;
      if (next.installments === undefined) next.installments = 3;
      if (next.installmentIncrease === undefined) next.installmentIncrease = 0;
      if (next.commercialEnding === undefined) next.commercialEnding = "none";
      if (mode === "cash_installment") {
        const style = next.estilo || {};
        const lineHeight = Number(style.tamanho_pt || 8) * 0.3528 * 1.05;
        const minimum =
          2 * lineHeight +
          2 * Number(style.padding_mm || 0) +
          2 * Number(style.borda_mm || 0) +
          1;
        const labelHeight = Number(this.label.altura_mm || 0);
        if (!labelHeight || Number(next.y_mm || 0) + minimum <= labelHeight) {
          next.altura_mm = Math.max(
            Number(next.altura_mm || 0),
            Math.ceil(minimum * 10) / 10
          );
        }
      }
      this.$emit("change-start");
      this.$emit("update", next);
    },
    get(path, fallback) {
      const value = path
        .split(".")
        .reduce(
          (current, part) => (current ? current[part] : undefined),
          this.element
        );
      return value === undefined || value === null ? fallback : value;
    },
    set(path, value) {
      const next = clone(this.element);
      const parts = path.split(".");
      const last = parts.pop();
      let current = next;
      parts.forEach((part) => {
        if (!current[part]) this.$set(current, part, {});
        current = current[part];
      });
      current[last] = value;
      this.$emit("change-start");
      this.$emit("update", next);
    },
    setNumber(path, value) {
      const number = Number(value);
      if (!Number.isNaN(number)) this.set(path, number);
    },
    toggle(path) {
      this.set(path, !this.get(path, false));
    },
    toggleTransparent() {
      this.set(
        "estilo.fundo",
        this.get("estilo.fundo", "transparent") === "transparent"
          ? "#FFFFFF"
          : "transparent"
      );
    },
    colorValue(value) {
      return /^#[0-9a-f]{6}$/i.test(String(value || "")) ? value : "#FFFFFF";
    },
  },
};
</script>

<style lang="scss" scoped>
.etq-inspector {
  padding: 20px;
}

.etq-panel-kicker {
  color: #7e8299;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.inspector-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding-bottom: 15px;
  border-bottom: 1px solid #eef0f5;
}

.min-w-0 {
  min-width: 0;
}

.element-name {
  width: 100%;
  padding: 1px 0;
  border: 0;
  border-bottom: 1px solid transparent;
  background: transparent;
  color: #181c32;
  font-size: 14px;
  font-weight: 700;

  &:focus {
    border-bottom-color: #8950fc;
    outline: none;
  }
}

.inspector-empty {
  min-height: 260px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px;
  text-align: center;

  h6 {
    color: #3f4254;
    font-weight: 700;
    margin: 14px 0 6px;
  }

  p {
    max-width: 230px;
    margin: 0;
    color: #7e8299;
    font-size: 12px;
    line-height: 1.55;
  }
}

.inspector-empty-icon {
  width: 52px;
  height: 52px;
  border-radius: 14px;
  background: #f3f0ff;
  color: #8950fc;
  display: grid;
  place-items: center;
  font-size: 20px;
}

label {
  color: #5e6278;
  font-size: 11px;
  font-weight: 600;
  margin-bottom: 5px;
}

.format-box,
.inspector-note {
  padding: 13px;
  border-radius: 9px;
  background: #f7f8fb;
  color: #5e6278;
  font-size: 11px;
}

.switch-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 9px 0;

  strong,
  small {
    display: block;
  }

  strong {
    color: #3f4254;
    font-size: 12px;
  }

  small {
    color: #a1a5b7;
    font-size: 10px;
    margin-top: 2px;
  }
}

.style-toolbar,
.rotation-buttons {
  display: flex;
  border: 1px solid #e4e6ef;
  border-radius: 7px;
  overflow: hidden;

  button {
    flex: 1;
    height: 32px;
    border: 0;
    border-right: 1px solid #e4e6ef;
    background: #fff;
    color: #7e8299;

    &:last-child {
      border-right: 0;
    }

    &.active,
    &:hover {
      background: #f1eaff;
      color: #6f42c1;
    }
  }
}

.content-alignment-box {
  padding: 13px;
  border: 1px solid #e4e6ef;
  border-radius: 9px;
  background: #f7f8fb;
}

.content-alignment-heading {
  margin-bottom: 12px;

  strong,
  small {
    display: block;
  }

  strong {
    color: #3f4254;
    font-size: 12px;
  }

  small {
    margin-top: 3px;
    color: #7e8299;
    font-size: 10px;
    line-height: 1.4;
  }
}

.alignment-toolbar {
  display: flex;
  overflow: hidden;
  border: 1px solid #d8dbe5;
  border-radius: 7px;

  button {
    flex: 1;
    min-width: 0;
    height: 38px;
    padding: 0 4px;
    border: 0;
    border-right: 1px solid #d8dbe5;
    background: #fff;
    color: #7e8299;
    font-size: 10px;

    &:last-child {
      border-right: 0;
    }

    i,
    span {
      display: block;
    }

    i {
      margin-bottom: 2px;
      font-size: 11px;
    }

    &.active,
    &:hover {
      background: #eee8ff;
      color: #8950fc;
    }
  }
}

.content-alignment-tip {
  display: block;
  margin-top: 10px;
  color: #7e8299;
  font-size: 9px;
  line-height: 1.4;
}

.toolbar-separator {
  width: 1px;
  background: #d8dbe5;
}

.color-field {
  height: 32px;
  border: 1px solid #e4e6ef;
  border-radius: 5px;
  display: flex;
  align-items: center;
  padding: 4px;
  gap: 6px;

  input[type="color"] {
    width: 24px;
    height: 22px;
    padding: 0;
    border: 0;
    background: transparent;
  }

  span {
    color: #7e8299;
    font-size: 9px;
  }
}

.transparent-toggle {
  width: 100%;
  height: 23px;
  border: 0;
  border-radius: 4px;
  background: #f3f6f9;
  color: #7e8299;
  font-size: 9px;

  &.active {
    color: #8950fc;
    background-image: linear-gradient(45deg, #e8e9ef 25%, transparent 25%),
      linear-gradient(-45deg, #e8e9ef 25%, transparent 25%),
      linear-gradient(45deg, transparent 75%, #e8e9ef 75%),
      linear-gradient(-45deg, transparent 75%, #e8e9ef 75%);
    background-size: 8px 8px;
  }
}

.position-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0 10px;
}

.position-actions {
  display: grid;
  gap: 8px;
}

::v-deep .inspector-tabs {
  margin-top: 15px;
  padding: 3px;
  border-radius: 8px;
  background: #f3f6f9;

  .nav-link {
    padding: 7px 4px;
    border-radius: 6px;
    color: #7e8299;
    font-size: 10px;
    font-weight: 700;

    &.active {
      background: #fff;
      color: #8950fc;
      box-shadow: 0 2px 6px rgba(31, 45, 61, 0.08);
    }
  }
}
</style>
